import { useEffect, useState } from "react";

export const LAUNCH_DATE = new Date("2026-11-14T00:00:00Z").getTime();

const pad = (n, len = 2) => String(n).padStart(len, "0");

const FORCE_LIVE =
  typeof window !== "undefined" && new URLSearchParams(window.location.search).has("live");

// Swap to the real channel URL on launch day.
export const CHANNEL_URL = "https://launchpad-tv.com/#trailer";

export default function Countdown() {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, []);

  const diff = FORCE_LIVE ? 0 : Math.max(0, LAUNCH_DATE - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);

  const units = [
    { label: "DAYS", value: pad(days, 3), testid: "countdown-days" },
    { label: "HOURS", value: pad(hours), testid: "countdown-hours" },
    { label: "MINUTES", value: pad(minutes), testid: "countdown-minutes" },
    { label: "SECONDS", value: pad(seconds), testid: "countdown-seconds", accent: true },
  ];

  if (diff <= 0) {
    return (
      <div data-testid="launch-countdown" className="relative w-full text-center">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] h-40 bg-signal/20 blur-[80px] rounded-full pointer-events-none" />
        <div className="relative flex items-center gap-3 mb-5">
          <span className="h-px flex-1 bg-white/10" />
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-signal" style={{ textShadow: "0 0 14px rgba(63,169,245,0.7)" }}>
            The signal is on air
          </span>
          <span className="h-px flex-1 bg-white/10" />
        </div>
        <div className="relative flex items-center justify-center gap-4">
          <span className="relative flex h-4 w-4">
            <span className="animate-live-dot absolute inline-flex h-full w-full rounded-full bg-signal" />
            <span className="relative inline-flex rounded-full h-4 w-4 bg-signal" />
          </span>
          <span
            data-testid="live-headline"
            className="font-head font-black leading-none text-4xl sm:text-6xl lg:text-7xl text-white"
            style={{ textShadow: "0 0 18px rgba(63,169,245,0.55), 0 0 64px rgba(63,169,245,0.28)" }}
          >
            WE ARE LIVE<span className="text-signal">.</span>
          </span>
        </div>
        <p className="relative mt-4 font-mono text-[10px] tracking-[0.3em] uppercase text-slate-400">
          193 countries. One channel. Go watch.
        </p>
        <a
          data-testid="live-watch-button"
          href={CHANNEL_URL}
          className="relative inline-flex items-center gap-3 mt-8 bg-signal text-obsidian font-bold font-mono text-xs tracking-[0.25em] uppercase px-10 py-4 signal-glow hover:bg-white transition-colors duration-300"
        >
          Watch Now
        </a>
      </div>
    );
  }

  return (
    <div data-testid="launch-countdown" className="relative w-full">
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] h-40 bg-signal/15 blur-[80px] rounded-full pointer-events-none" />
      <div className="relative flex items-center gap-3 mb-5">
        <span className="h-px flex-1 bg-white/10" />
        <span className="font-mono text-[10px] sm:text-[11px] tracking-[0.2em] sm:tracking-[0.3em] uppercase text-signal text-center whitespace-nowrap" style={{ textShadow: "0 0 14px rgba(63,169,245,0.7)" }}>
          <span className="sm:hidden">Signal live — 11.14.2026</span>
          <span className="hidden sm:inline">Global signal goes live — 11.14.2026</span>
        </span>
        <span className="h-px flex-1 bg-white/10" />
      </div>
      <div className="relative flex items-start justify-center gap-3 sm:gap-5">
        {units.map((u, i) => (
          <div key={u.label} className="flex items-start gap-3 sm:gap-5">
            <div className="text-center">
              <div
                data-testid={u.testid}
                className={`font-head font-extrabold tabular-nums leading-none text-4xl sm:text-6xl lg:text-7xl ${
                  u.accent ? "text-signal" : "text-white"
                }`}
                style={{
                  textShadow: u.accent
                    ? "0 0 18px rgba(63,169,245,0.8), 0 0 64px rgba(63,169,245,0.4)"
                    : "0 0 18px rgba(63,169,245,0.55), 0 0 64px rgba(63,169,245,0.28)",
                }}
              >
                {u.value}
              </div>
              <div className="font-mono text-[9px] sm:text-[10px] tracking-[0.3em] text-slate-400 mt-2.5">
                {u.label}
              </div>
            </div>
            {i < units.length - 1 && (
              <span
                className="animate-blink font-head font-extrabold text-3xl sm:text-5xl lg:text-6xl text-signal leading-none mt-1"
                style={{ textShadow: "0 0 18px rgba(63,169,245,0.7)" }}
              >
                :
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
